import { ImmutableStore } from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import moment from 'moment';
import { scaleLinear } from 'd3-scale';
import Immutable from 'immutable';
import { keyText } from '../intl/ActionMessages';
import { startOfDay } from '../utils/Date';
import { parseJsonIfNeeded } from '../utils/UrlParamParsers';
import { GuideDelivery } from '../model/Guide';
import { getComparator } from '../utils/listItemUtils';
import startcase from 'lodash.startcase';

const key2label = e => keyText[e] || keyText[`${e}.completed`] || startcase(String(e).replace(/button|clicked|completed/gi, ''));

const sortKeys = [
  {
    key: 'recommended',
    value: 'Recommendations'
  }, {
    key: 'recommendedPercentage',
    value: 'Recommendation %'
  }
];

function defaultState() {
  return {
    data: [],
    sortKey: 'recommendedPercentage',
    actionType: 'percentage',
    sortKeys,
    actionTypes: [],
    ascending: false,
    guideType: 'both',
    guideDelivery: GuideDelivery.branch,
    loadingData: false,
    loadingHistory: false,
    historyData: [],
    selectedBranchHierarchy: null,
    customerType: 'both'
  };
}

const percentageTest = /Percentage$/;

const domains = {
};

function setFocused(store, leaderData = Immutable.Seq(), focusedKey) {
  const focusedObj = leaderData.findEntry(leader => {
    return leader.get('key') === focusedKey;
  });

  if (focusedObj) {
    store.focusedObj = focusedObj[1];
    store.focusedIndex = focusedObj[0] + 1;
  } else {
    delete store.focusedObj;
    delete store.focusedIndex;
  }
}

function sortData(data, sortKey, ascending) {
  const comparator = getComparator(sortKey, ascending);
  return data.sort(comparator);
}

class ProductActionStore extends ImmutableStore {
  static storeName = 'ProductActionStore';

  static handlers = {
    [Actions.PRODUCT_ACTION__DATA_LOADED]: 'handleProductActionDataChanged',
    [Actions.PRODUCT_ACTION__QUERY_CHANGED]: 'handleProductActionQueryChanged',
    [Actions.PRODUCT_ACTION__DATA_LOAD_STARTED]: 'handleLoadingData',
    [Actions.AUTHENTICATION__CHECK_COMPLETED]: 'handleAuth',
    [Actions.AUTHENTICATION__COMPLETED]: 'handleAuth',
  };

  constructor(dispatcher) {
    super(dispatcher);

    const now = startOfDay();
    const before = startOfDay().subtract(1, 'month');

    this.startDate = before.toDate();
    this.endDate = now.toDate();
    this.setState(defaultState());
  }

  handleProductActionQueryChanged(query) {
    const defaults = defaultState();
    const {
      sortKey = defaults.sortKey,
      sortDirection = defaults.sortDirection,
      focused,
      startDate = startOfDay().subtract(1, 'month'),
      endDate = startOfDay(),
      guideType = defaults.guideType,
      guideDelivery = defaults.guideDelivery,
      actionType = defaults.actionType,
      customerType = defaults.customerType,
      selectedBranchHierarchy
    } = query;

    if (this.get('startDate') !== startDate || this.get('endDate') !== endDate) {
      this.startDate = new Date(startDate);
      this.endDate = new Date(endDate);
      this.emitChange();
    }

    const ascending = sortDirection === 'ascending';
    const data = sortData(this.get('data'), sortKey, ascending);
    setFocused(this, data, focused);

    this.mergeState({
      ascending,
      sortKey,
      guideType,
      data,
      focused,
      startDate,
      endDate,
      guideDelivery,
      actionType,
      customerType,
      selectedBranchHierarchy: parseJsonIfNeeded(selectedBranchHierarchy)
    });
  }

  handleLoadingData(type) {
    this.mergeState({
      [`${type}Loading`]: true
    });
  }

  handleProductActionDataChanged(payload) {
    const { leaderData, historyData } = payload;

    console.info('Updating leaderboard store state', payload);

    const state = {};

    if (historyData) {
      historyData.forEach(d => {
        d.timestamp = moment().year(d.year).dayOfYear(d.day).toDate();
      });

      state.historyData = historyData;
      state.historyLoading = false;
    }

    if (leaderData) {
      const sortKey = this.get('sortKey');
      const ascending = this.get('ascending');
      const immutableData = Immutable.fromJS(leaderData.filter(a => a.recommended));
      let actionSortKeys = Immutable.Set();
      const actionKeys = [];
      const actionTypes = {};
      immutableData.forEach(d => {
        d.get('actions').forEach((actionValue, actionKey) => {
          if (!actionTypes[actionKey]) {
            actionTypes[actionKey] = {
              values: Immutable.Set()
            };
          }
          const actionType = actionTypes[actionKey];
          actionValue.forEach((value, key) => {
            actionType.values = actionType.values.add(key);
            const actionSortKey = `${actionKey}.${key}`;
            if (!actionSortKeys.contains(actionSortKey)) {
              actionSortKeys = actionSortKeys.add(actionSortKey);
              actionKeys.push({
                key: `actions.${actionSortKey}`,
                value: key2label(actionSortKey)
              });
            }
          });
        });
      });
      const sortedActionKeys = Immutable.fromJS(actionKeys).sortBy(e => e.get('value')).toJS();
      const sortedData = sortData(immutableData, sortKey, ascending);
      setFocused(this, sortedData, this.get('focused'));
      state.data = sortedData;
      state.actionTypes = actionTypes;
      state.sortKeys = sortKeys.concat(sortedActionKeys);
      state.leaderLoading = false;
    }

    this.mergeState(state);
  }

  getLeaderData() {
    const sortKey = this.get('sortKey');
    const sorted = this.get('data');
    const scale = scaleLinear();
    let domain = domains[sortKey];

    if (percentageTest.test(sortKey)) {
      domain = [0, 1];
    }
    if (!domain) {
      const values = sorted.map(e => {
        return e.getIn(sortKey.split('.')) || 0;
      }).toJS();
      let min = 0, max = 1;
      if (values.length) {
        min = Math.min(...values);
        max = Math.max(...values);
      }

      domain = [min, max];
    }

    const leaderScale = scale.domain(domain).nice().range([0, 100]).clamp(true);

    return {
      leaderData: sorted.toJS(),
      leaderScale
    };
  }

  isAscendingSort() {
    return this.get('ascending');
  }

  getFocusedLeader() {
    return this.focusedObj;
  }

  getFocusedIndex() {
    return this.focusedIndex || 0;
  }

  getLeaderCount() {
    const data = this.get('data');
    return data ? data.size : 0;
  }

  getSortKey() {
    return this.get('sortKey');
  }

  getCustomerType() {
    return this.get('customerType');
  }

  getSortKeys() {
    return this.get('sortKeys').toJS();
  }

  getGuideType() {
    return this.get('guideType');
  }

  handleAuth = ({ clientInfo: { branchHierarchyTerms, branchHierarchy } = {} } = {}) => {
    if (branchHierarchyTerms && branchHierarchy) {
      this.mergeState({
        branchHierarchy,
        branchHierarchyTerms
      });
    }
  };

  getBranchHierarchy = () => {
    return this.get('branchHierarchy');
  };

  getBranchHierarchyTerms = () => {
    return this.get('branchHierarchyTerms');
  };

  getSelectedBranchHierarchy = () => {
    return this.get('selectedBranchHierarchy');
  };

  getHistoryData() {
    return this.get('historyData').toJS();
  }

  getSelectedDateRange() {
    return {
      start: this.startDate,
      end: this.endDate
    };
  }

  getActionType() {
    return this.get('actionType');
  }

  getGuideDelivery() {
    return this.get('guideDelivery');
  }

  isLoading() {
    return this.get('leaderLoading') || this.get('historyLoading');
  }

  rehydrate(state) {
    super.rehydrate(state._state);

    this.startDate = new Date(state.startDate);
    this.endDate = new Date(state.endDate);
    this.focusedObj = state.focusedObj ? Immutable.fromJS(state.focusedObj) : null;
    this.focusedIndex = state.focusedIndex;
  }

  dehydrate() {
    return {
      _state: this._state,
      startDate: this.startDate,
      endDate: this.endDate,
      focusedObj: this.focusedObj,
      focusedIndex: this.focusedIndex
    };
  }
}

export default ProductActionStore;



// WEBPACK FOOTER //
// ./src/stores/ProductActionStore.js